import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, Printer, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, orderFileName } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, GoldButton, GhostButton, Empty, Field, Select, ProductImage, inputCls } from "@/components/common";

const suggested = (p) => Math.max(p.min_threshold * 2 - p.quantity, 1);

export const downloadPdf = async (url, filename) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (e) { toast.error("Download del PDF non riuscito"); }
};

export const downloadOrderPdf = (o) => downloadPdf(`/orders/${o.id}/pdf`, orderFileName(o));

export const downloadOrderCsv = (o) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = [["Prodotto", "Marca", "Quantità da ordinare", "Giacenza attuale"], ...o.items.map((i) => [i.name, i.brand, i.quantity, i.stock])];
  const head = [`Ordine ${o.number}`, `Fornitore: ${o.supplier || "-"}`, `Data: ${fmtDate(o.created_at)}`, "Estetica e Benessere di Micol Pennacchietti", ""];
  const csv = "\ufeff" + [...head.map((h) => esc(h)), ...rows.map((r) => r.map(esc).join(";"))].join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = orderFileName(o, "csv");
  a.click();
  URL.revokeObjectURL(a.href);
};

export const OrderDialog = ({ open, onOpenChange, products, onCreated }) => {
  const [sel, setSel] = useState({});
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [onlyLow, setOnlyLow] = useState(true);
  const suppliers = useMemo(() => [...new Set(products.map((p) => p.supplier).filter(Boolean))].sort(), [products]);

  useEffect(() => {
    if (!open) { return; }
    setSel(Object.fromEntries(products.filter((p) => p.quantity <= p.min_threshold).map((p) => [p.id, suggested(p)])));
    setSupplier(""); setNotes(""); setOnlyLow(true);
  }, [open, products]);

  const shown = products.filter((p) => (!onlyLow || p.quantity <= p.min_threshold || sel[p.id] !== undefined) && (!supplier || p.supplier === supplier));
  const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p.id] !== undefined) delete n[p.id]; else n[p.id] = suggested(p); return n; });
  const chosen = shown.filter((p) => sel[p.id] !== undefined && Number(sel[p.id]) > 0);

  const create = async (mode) => {
    if (!chosen.length) return toast.error("Seleziona almeno un prodotto con quantità");
    const win = mode === "pdf" ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/orders", { supplier, notes, items: chosen.map((p) => ({ product_id: p.id, name: p.name, brand: p.brand, quantity: Number(sel[p.id]), stock: p.quantity })) });
      toast.success(`Ordine ${data.number} creato`);
      onOpenChange(false);
      onCreated?.();
      if (mode === "csv") downloadOrderCsv(data);
      else if (mode === "download") downloadOrderPdf(data);
      else if (win) win.location.href = `/magazzino/ordini/${data.id}/stampa`;
      else window.location.href = `/magazzino/ordini/${data.id}/stampa`;
    } catch (e) { win?.close(); toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden min-w-0" data-testid="order-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">Ordine al fornitore</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 min-w-0">
          <Field label="Fornitore">
            <input list="suppliers-list" className={inputCls} value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Nome fornitore" data-testid="order-supplier-input" />
            <datalist id="suppliers-list">{suppliers.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <Field label="Mostra">
            <Select value={onlyLow ? "low" : "all"} onChange={(e) => setOnlyLow(e.target.value === "low")} options={[{ value: "low", label: "Solo prodotti in esaurimento" }, { value: "all", label: "Tutti i prodotti" }]} data-testid="order-filter-select" />
          </Field>
        </div>
        {supplier && !suppliers.includes(supplier) && <p className="text-xs text-[#9E958A]">Nessun prodotto ha questo fornitore: vengono mostrati solo i prodotti con fornitore uguale. Svuota il campo per vederli tutti.</p>}
        {shown.length === 0 ? <Empty testId="order-empty">Nessun prodotto da mostrare.</Empty> : (
          <ul className="divide-y divide-[#E8E1D7] min-w-0">
            {shown.map((p) => {
              const on = sel[p.id] !== undefined;
              return (
                <li key={p.id} className="flex min-w-0 items-center gap-2 sm:gap-3 py-3" data-testid={`order-row-${p.id}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(p)} className="h-4 w-4 accent-[#C5A059]" data-testid={`order-check-${p.id}`} />
                  <ProductImage path={p.photo_path} className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="truncate text-xs text-[#6E675F]">{[p.brand, p.supplier].filter(Boolean).join(" · ") || "—"} · giacenza {p.quantity} / soglia {p.min_threshold}</div>
                  </div>
                  <input type="number" min="1" disabled={!on} className={`${inputCls} !w-20 text-right`} value={on ? sel[p.id] : ""} onChange={(e) => setSel({ ...sel, [p.id]: e.target.value })} data-testid={`order-qty-${p.id}`} />
                </li>
              );
            })}
          </ul>
        )}
        <Field label="Note per il fornitore"><textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes-input" /></Field>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-[#6E675F]" data-testid="order-selected-count">{chosen.length} prodotti selezionati</span>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <GhostButton onClick={() => create("csv")} data-testid="order-create-csv"><FileDown className="h-4 w-4" /> Crea e scarica Excel</GhostButton>
            <GhostButton onClick={() => create("download")} data-testid="order-create-download"><FileDown className="h-4 w-4" /> Crea e scarica PDF</GhostButton>
            <GoldButton onClick={() => create("pdf")} data-testid="order-create-pdf"><Printer className="h-4 w-4" /> Crea e apri anteprima</GoldButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const OrdersList = ({ orders, reload }) => {
  const nav = useNavigate();
  const del = async (o) => { if (window.confirm(`Eliminare l'ordine ${o.number}?`)) { await api.delete(`/orders/${o.id}`); reload(); } };
  if (!orders.length) return <Empty testId="orders-empty">Nessun ordine creato. Usa "Ordine fornitore" per prepararne uno.</Empty>;
  return (
    <Panel className="p-2">
      <ul className="divide-y divide-[#E8E1D7]">
        {orders.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`order-list-row-${o.id}`}>
            <div className="w-28 font-mono text-xs text-[#6E675F]">{o.number}</div>
            <div className="flex-1 min-w-[150px]"><div className="font-medium">{o.supplier || "Fornitore non indicato"}</div><div className="text-xs text-[#6E675F]">{fmtDate(o.created_at)} · {o.items.length} prodotti</div></div>
            <GhostButton onClick={() => nav(`/magazzino/ordini/${o.id}/stampa`)} className="px-3 py-1.5" data-testid={`order-print-${o.id}`}><Printer className="h-3.5 w-3.5" /> Apri</GhostButton>
            <GhostButton onClick={() => downloadOrderPdf(o)} className="px-3 py-1.5" data-testid={`order-download-${o.id}`}><FileDown className="h-3.5 w-3.5" /> Scarica PDF</GhostButton>
            <GhostButton onClick={() => downloadOrderCsv(o)} className="px-3 py-1.5" data-testid={`order-csv-${o.id}`}><FileDown className="h-3.5 w-3.5" /> Excel</GhostButton>
            <button onClick={() => del(o)} className="text-[#6E675F] hover:text-red-700" data-testid={`order-delete-${o.id}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </Panel>
  );
};
